import AccessTime from '@mui/icons-material/AccessTime';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import HelpOutline from '@mui/icons-material/HelpOutline';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ExternalExportStatus } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { Ticket } from '~hooks/useTickets/models';
import { Nullable } from '~types/Nullable';
import isEnumValue from '~utils/isEnumValue';

const INDICATOR_SIZE = '11px';

const STATUS_ICONS = {
  [ExternalExportStatus.EXPORTED]: CheckCircleOutline,
  [ExternalExportStatus.NEEDS_ATTENTION]: WarningAmber,
  [ExternalExportStatus.NOT_APPLICABLE]: AccessTime,
  [ExternalExportStatus.PENDING]: AccessTime,
  [ExternalExportStatus.UNKNOWN_OUTCOME]: HelpOutline,
} satisfies Record<ExternalExportStatus, typeof AccessTime>;

const STATUS_COLORS = {
  [ExternalExportStatus.EXPORTED]: (theme: Theme) => theme.palette.success.main,
  [ExternalExportStatus.NEEDS_ATTENTION]: (theme: Theme) => theme.semantic.statusWarning,
  [ExternalExportStatus.NOT_APPLICABLE]: (theme: Theme) => theme.palette.text.secondary,
  [ExternalExportStatus.PENDING]: (theme: Theme) => theme.palette.text.secondary,
  [ExternalExportStatus.UNKNOWN_OUTCOME]: (theme: Theme) => theme.palette.error.main,
} satisfies Record<ExternalExportStatus, (theme: Theme) => string>;

const STATUS_DETAILS = {
  [ExternalExportStatus.EXPORTED]: (ticket: Ticket) => ticket.externalExportReason,
  [ExternalExportStatus.NEEDS_ATTENTION]: (ticket: Ticket) => ticket.externalExportReason,
  [ExternalExportStatus.NOT_APPLICABLE]: () =>
    t('approvals.ticket_export.not_applicable_detail'),
  [ExternalExportStatus.PENDING]: (ticket: Ticket) => ticket.externalExportReason,
  [ExternalExportStatus.UNKNOWN_OUTCOME]: () =>
    t('approvals.ticket_export.unknown_outcome_detail'),
} satisfies Record<ExternalExportStatus, (ticket: Ticket) => Nullable<string>>;

const getExport = (ticket: Ticket) => {
  const status = ticket.externalExportStatus;

  // A status newer than this build's spec has no entry in the maps below.
  if (!ticket.vetaCenterBound || !status || !isEnumValue(ExternalExportStatus, status)) {
    return null;
  }

  const detail = STATUS_DETAILS[status](ticket);

  return {
    detail,
    status,
    warning: status === ExternalExportStatus.EXPORTED ? detail : null,
  };
};

export function TicketExportIndicator({ ticket }: { ticket: Ticket }) {
  const ticketExport = getExport(ticket);

  if (!ticketExport || ticketExport.status === ExternalExportStatus.NOT_APPLICABLE) {
    return null;
  }

  const { detail, status, warning } = ticketExport;
  const Icon = STATUS_ICONS[status];
  const label = t(`approvals.ticket_export.states.${status}`);

  return (
    <Tooltip title={detail ?? ''}>
      <Box
        aria-label={detail ? `${label}. ${detail}` : label}
        data-test-id={`ticket-export-indicator-${status}`}
        role="img"
        tabIndex={0}
        sx={(theme) => ({
          alignItems: 'center',
          color: STATUS_COLORS[status](theme),
          display: 'flex',
          gap: 0.25,
          minWidth: 0,
        })}
      >
        <Icon sx={{ fontSize: INDICATOR_SIZE, flexShrink: 0 }} />

        <Typography
          sx={{
            fontSize: INDICATOR_SIZE,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>

        {warning && (
          <WarningAmber
            data-test-id="ticket-export-warning"
            sx={(theme) => ({
              color: theme.semantic.statusWarning,
              fontSize: INDICATOR_SIZE,
              flexShrink: 0,
            })}
          />
        )}
      </Box>
    </Tooltip>
  );
}

interface TicketExportBlockProps {
  sx?: SxProps<Theme>;
  ticket: Ticket;
}

export function TicketExportBlock({ sx, ticket }: TicketExportBlockProps) {
  const ticketExport = getExport(ticket);

  if (!ticketExport) {
    return null;
  }

  const { detail, status, warning } = ticketExport;
  const Icon = STATUS_ICONS[status];

  return (
    <Box
      data-test-id="ticket-export-status"
      sx={[
        {
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          px: 2,
          py: 1.5,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'uppercase',
        }}
      >
        {t('approvals.ticket_export.label')}
      </Typography>

      <Chip
        icon={<Icon />}
        label={t(`approvals.ticket_export.chips.${status}`)}
        size="small"
        sx={(theme) => ({
          backgroundColor: theme.semantic.surfaceSunken,
          color: STATUS_COLORS[status](theme),
          '& .MuiChip-icon': { color: 'inherit' },
        })}
      />

      {detail && (
        <Typography
          sx={(theme) => ({
            color: warning ? theme.semantic.statusWarning : 'text.secondary',
            fontSize: '14px',
          })}
        >
          {detail}
        </Typography>
      )}
    </Box>
  );
}
